/**
 * SecondarySidebar Component
 * Phase 68 M4: 230px section sidebar per locked design 001-global-shell.
 * Header: icon (18px) + title (13px 600)
 * Nav items: padding 9px 16px 9px 20px, 13px font, 3px left border on active
 * Footer: "← Inicio" (12px) with back arrow (14px)
 * Per L2: Animate sidebar transitions
 */

import React, { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { alpha, Box, Typography } from '@mui/material'
import { useAppAccess } from '@/hooks/useAppAccess'
import Chat from '@mui/icons-material/Chat'
import LibraryBooks from '@mui/icons-material/LibraryBooks'
// Path-based imports for icons not declared in mui.d.ts
import Work from '@mui/icons-material/Work'
import Settings from '@mui/icons-material/Settings'
import PersonSearch from '@mui/icons-material/PersonSearch'
import UploadFile from '@mui/icons-material/UploadFile'
import Assignment from '@mui/icons-material/Assignment'
import Business from '@mui/icons-material/Business'
import People from '@mui/icons-material/People'
import Person from '@mui/icons-material/Person'
import ArrowBack from '@mui/icons-material/ArrowBack'
import Explore from '@mui/icons-material/Explore'
import Dashboard from '@mui/icons-material/Dashboard'
import Shield from '@mui/icons-material/Shield'
import CreditCard from '@mui/icons-material/CreditCard'
import Badge from '@mui/icons-material/Badge'  // Phase 113: Colaboradores (employees)

import type { SidebarConfig, SidebarItem } from '@/config/sidebarConfigs'

/** Map icon name strings to MUI icon components with 18px size per prototype */
const ICON_MAP: Record<string, React.ReactNode> = {
  Work: <Work sx={{ fontSize: 18 }} />,
  Chat: <Chat sx={{ fontSize: 18 }} />,
  Settings: <Settings sx={{ fontSize: 18 }} />,
  PersonSearch: <PersonSearch sx={{ fontSize: 18 }} />,
  UploadFile: <UploadFile sx={{ fontSize: 18 }} />,
  Assignment: <Assignment sx={{ fontSize: 18 }} />,
  Business: <Business sx={{ fontSize: 18 }} />,
  People: <People sx={{ fontSize: 18 }} />,
  LibraryBooks: <LibraryBooks sx={{ fontSize: 18 }} />,
  Person: <Person sx={{ fontSize: 18 }} />,
  Explore: <Explore sx={{ fontSize: 18 }} />,  // L2: Compass icon for Compás sidebar
  Dashboard: <Dashboard sx={{ fontSize: 18 }} />,  // L2: Platform dashboard placeholder
  Shield: <Shield sx={{ fontSize: 18 }} />,  // Phase 70: Permissions
  CreditCard: <CreditCard sx={{ fontSize: 18 }} />,
  Badge: <Badge sx={{ fontSize: 18 }} />,  // Phase 113: Colaboradores (employee ID badge)
}

interface SecondarySidebarProps {
  config: SidebarConfig
}

const SecondarySidebar: React.FC<SecondarySidebarProps> = ({ config }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { hasAccess } = useAppAccess()

  // Phase 76: Filter items based on app access (replaces requiresSuperAdmin/isSuperAdmin)
  const filteredItems = useMemo(() => {
    return config.items.filter((item) => {
      if (item.requiredApp && !hasAccess(item.requiredApp)) return false
      return true
    })
  }, [config.items, hasAccess])

  const isActive = (item: SidebarItem) => {
    if (item.exact || item.path === '/talent-pipeline') return location.pathname === item.path
    return location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  }

  return (
    <Box
      component="nav"
      sx={{
        width: 230,
        minWidth: 230,
        height: '100%',
        borderRight: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        // Per L2: Animate sidebar transitions
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease',
      }}
    >
      {/* Section header — 20px 16px 12px padding per prototype */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1, // 8px
          px: 2, // 16px
          pt: 2.5, // 20px
          pb: 1.5, // 12px
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ color: 'primary.main', display: 'flex' }}>
          {ICON_MAP[config.header.icon]}
        </Box>
        <Typography
          sx={{
            fontSize: 13, // 13px per prototype
            fontWeight: 600,
            color: 'text.primary',
            letterSpacing: '0.01em',
          }}
        >
          {config.header.label}
        </Typography>
      </Box>

      {/* Nav items — 8px top/bottom padding per prototype */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        {filteredItems.map((item, index) => {
          const disabled = !item.path  // L2: Empty path = disabled placeholder
          const active = !disabled && isActive(item)

          return (
            <Box
              key={item.path || `disabled-${index}`}
              role={disabled ? undefined : 'link'}
              tabIndex={disabled ? -1 : 0}
              onClick={disabled ? undefined : () => navigate(item.path)}
              onKeyDown={disabled ? undefined : (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  navigate(item.path)
                }
              }}
              sx={(theme) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 1, // 8px between icon and label
                // Per prototype: padding 9px 16px 9px 20px
                py: 1.125, // 9px
                pl: 2.5, // 20px
                pr: 2, // 16px
                cursor: disabled ? 'default' : 'pointer',
                fontSize: 13, // 13px per prototype
                fontWeight: active ? 600 : 400,
                fontStyle: disabled ? 'italic' : 'normal',  // L2: Italic for disabled items
                color: disabled ? 'text.disabled' : active ? 'primary.main' : 'text.secondary',
                bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                // Per prototype: 3px left border
                borderLeft: `3px solid ${active ? theme.palette.primary.main : 'transparent'}`,
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
                '&:hover': disabled ? {} : {
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  color: 'text.primary',
                },
              })}
            >
              {/* BUG-029 fix: Render nav item icons */}
              <Box sx={{ display: 'flex', opacity: 0.7 }}>
                {ICON_MAP[item.icon]}
              </Box>
              {item.label}
            </Box>
          )
        })}
      </Box>

      {/* Footer: back to home — 12px 16px padding per prototype */}
      <Box
        role="link"
        tabIndex={0}
        onClick={() => navigate('/')}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            navigate('/')
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75, // 6px
          px: 2, // 16px
          py: 1.5, // 12px
          borderTop: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer',
          color: 'text.disabled',
          fontSize: 12, // 12px per prototype
          transition: 'all 0.15s ease',
          '&:hover': {
            color: 'text.secondary',
            bgcolor: 'action.hover',
          },
        }}
      >
        <ArrowBack sx={{ fontSize: 14 }} />
        Inicio
      </Box>
    </Box>
  )
}

export default SecondarySidebar
